/**
 * Zugriff auf die Lese-API.
 *
 * Zwei Dinge macht dieser Klient, die man sonst in jeder Seite wiederholen
 * müsste: er hängt den Mandanten an jede Anfrage, und er übersetzt die
 * Fehlerantworten der API in eine Ausnahme mit lesbarem deutschen Text.
 *
 * Abgebrochene Anfragen (AbortController) sind kein Fehler — wer schnell
 * tippt, löst zehn Suchen aus und will neun davon verwerfen.
 */

export interface Seite<T> {
  zeilen: T[];
  seite: number;
  groesse: number;
  gesamt: number;
  seiten: number;
  /**
   * Die Version einer **Menge**, wo eine Liste eine ist.
   *
   * Bisher nur an `GET /api/konten/:kontoSk/bereiche`: die Präfixe eines
   * Kontos sind keine Seite eines Bestands, sondern die ganze Menge, und
   * ihr Schreibweg verlangt sie seit dem 27.08.2026 als `If-Match`.
   * Begründung in `apps/api/src/mengenversion.ts`.
   */
  __version?: string;
}

export class ApiFehler extends Error {
  constructor(
    public readonly status: number,
    nachricht: string,
  ) {
    super(nachricht);
    this.name = 'ApiFehler';
  }
}

const BASIS = import.meta.env['VITE_API_BASIS'] ?? '';

/**
 * Was wirklich schiefging, wenn `fetch` selbst wirft.
 *
 * Bis zum 14.08.2026 stand hier fuer **jeden** solchen Fall derselbe Satz:
 * „Die Schnittstelle ist nicht erreichbar." Das ist eine Diagnose, und zwar
 * meistens die falsche. `fetch` wirft auch, wenn die Gegenstelle sehr wohl
 * antwortet — bei einer abgelehnten CORS-Vorabfrage, bei einer Verbindung,
 * die mitten in der Antwort abreisst, bei einem ungueltigen Kopfwert. Wer
 * daraufhin die Erreichbarkeit prueft, findet nichts und sucht am falschen
 * Ende; genau das ist beim Speichern einer Rolle passiert.
 *
 * Der Browser sagt nicht viel — aus Sicherheitsgruenden nennt er den Grund
 * einer CORS-Ablehnung nicht —, aber sein Text ist mehr als nichts, und
 * Verfahren und Pfad machen die Meldung ueberhaupt erst nachvollziehbar.
 */
/**
 * Prüft die Kopfzeilen, **bevor** `fetch` sie ablehnt.
 *
 * Am 18.08.2026 scheiterte das Speichern einer Rolle mit
 *
 *     Failed to execute 'fetch' on 'Window': Invalid value
 *
 * Der Browser sagt damit: einer der Kopfwerte ist unzulässig. **Welcher,
 * sagt er nicht.** Ein Kopfwert darf keine Steuerzeichen enthalten, keinen
 * Zeilenumbruch und nichts oberhalb von Latin-1; verletzt einer davon die
 * Regel, wirft `fetch`, bevor irgendetwas hinausgeht.
 *
 * Der häufigste Weg dorthin ist kein Tippfehler, sondern ein Wert, der aus
 * den Daten stammt und nie als Kopf gedacht war — hier `if-match` mit der
 * Zeilenversion. Deshalb wird der Kopf benannt und sein Wert gezeigt, statt
 * die Fehlersuche wieder bei der Erreichbarkeit anfangen zu lassen.
 */
function kopfPruefen(kopf: Record<string, string>, methode: string, pfad: string): void {
  for (const [name, wert] of Object.entries(kopf)) {
    const roh = String(wert);
    /* Erlaubt sind sichtbare Zeichen, Leerzeichen und Tabulator im Bereich
       bis 0xFF. Alles andere — Umbruch, Nullbyte, Zeichen jenseits von
       Latin-1 — weist der Browser zurück. */
    const schlecht = [...roh].findIndex((z) => {
      const c = z.codePointAt(0) ?? 0;
      return c > 0xFF || (c < 0x20 && c !== 0x09);
    });
    if (schlecht === -1 && roh === roh.trim()) continue;

    const zeichen = schlecht === -1 ? '' : roh[schlecht] ?? '';
    const code = schlecht === -1 ? '' : (zeichen.codePointAt(0) ?? 0).toString(16);
    throw new ApiFehler(0,
      `Die Anfrage ${methode} ${pfad} wurde nicht abgeschickt: der Kopf `
      + `„${name}" trägt einen unzulässigen Wert `
      + (schlecht === -1
        ? '(Leerraum am Anfang oder Ende).'
        : `(Zeichen ${schlecht + 1} ist U+${code.toUpperCase().padStart(4, '0')}).`)
      + ` Wert: ${JSON.stringify(roh.slice(0, 80))}`);
  }
}

function netzfehler(methode: string, pfad: string, fehler: unknown): string {
  const grund = fehler instanceof Error && fehler.message ? fehler.message : 'kein Grund genannt';
  return `Die Anfrage ${methode} ${pfad} kam nicht zustande (${grund}). `
    + 'Das heißt nicht zwingend, dass die Schnittstelle steht — auch eine '
    + 'abgewiesene Vorabfrage, eine abgerissene Verbindung oder ein '
    + 'ungültiger Kopfwert sehen hier genauso aus.';
}

/** Wandelt ein Filterobjekt in eine Abfragezeichenfolge, leere Werte fallen weg. */
export function alsAbfrage(werte: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [name, wert] of Object.entries(werte)) {
    if (wert === undefined || wert === null || wert === '') continue;
    if (Array.isArray(wert)) {
      if (wert.length === 0) continue;
      p.set(name, wert.join(','));
    } else if (typeof wert === 'boolean') {
      p.set(name, wert ? 'ja' : 'nein');
    } else {
      p.set(name, String(wert));
    }
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

interface Optionen {
  mandant?: string | undefined;
  signal?: AbortSignal | undefined;
  token?: string | undefined;
}

/**
 * Der Stichtag eines Rueckblicks, als Zeitpunkt in UTC — oder `null`.
 *
 * Eine Modulvariable und keine Eigenschaft an jedem Aufruf: `holen` hat in
 * dieser Anwendung ueber hundert Aufrufstellen, und ein Wert, der an jeder
 * einzelnen haengt, fehlt frueher oder spaeter an einer. Gesetzt wird sie
 * ausschliesslich von `bausteine/Standrahmen.tsx`.
 */
let stichtag: string | null = null;

/** Den Stichtag setzen. Nur der Standrahmen ruft das. */
export function standSetzen(zeitpunkt: string | null): void {
  stichtag = zeitpunkt;
}

/** Der gesetzte Stichtag — fuer Pruefungen und Meldungen. */
export function standLesen(): string | null {
  return stichtag;
}

export async function holen<T>(pfad: string, optionen: Optionen = {}): Promise<T> {
  const kopf: Record<string, string> = { accept: 'application/json' };
  if (optionen.mandant) kopf['x-mandant'] = optionen.mandant;
  if (stichtag) kopf['x-stand'] = stichtag;
  if (optionen.token) kopf['authorization'] = `Bearer ${optionen.token}`;

  /* Konto des Entwicklungsbetriebs.
     Dort gibt es keine Anmeldung; ohne diesen Kopf laesst sich die Oberflaeche
     immer nur mit dem obersten Konto bedienen — und damit nie pruefen, was
     jemand mit weniger Rechten sieht. Die **echte** Schnittstelle liest den
     Kopf nicht: dort entscheidet das Token, und ein Browser darf sich seine
     Rechte nicht selbst aussuchen. */
  if (import.meta.env.DEV) {
    try {
      const konto = localStorage.getItem('iammatrix.konto');
      if (konto) kopf['x-konto'] = konto;
    } catch { /* kein Speicher verfuegbar */ }
  }

  let antwort: Response;
  try {
    antwort = await fetch(`${BASIS}${pfad}`, {
      headers: kopf,
      ...(optionen.signal ? { signal: optionen.signal } : {}),
    });
  } catch (fehler) {
    if (fehler instanceof DOMException && fehler.name === 'AbortError') throw fehler;
    throw new ApiFehler(0, netzfehler('GET', pfad, fehler));
  }

  if (!antwort.ok) {
    let text = `Die Schnittstelle antwortete mit ${antwort.status}.`;
    try {
      const koerper = (await antwort.json()) as { fehler?: string };
      if (koerper.fehler) text = koerper.fehler;
    } catch {
      // Antwort ohne JSON-Körper — die Vorgabe oben bleibt stehen
    }
    throw new ApiFehler(antwort.status, text);
  }

  /* Eine Antwort mit 200, die sich nicht lesen laesst, ist etwas anderes als
     ein Fehler der Schnittstelle — und `JSON.parse` sagt das in einer Sprache,
     die niemandem hilft: „Unterminated string in JSON at position 96420".
     Gemeint ist damit ein Rumpf, der mitten in einer Zeichenkette aufhoert,
     also eine **abgeschnittene** Uebertragung. Die Meldung nennt das jetzt
     beim Namen und gibt an, wie weit sie kam; das ist die Angabe, mit der sich
     der Abbruch eingrenzen laesst. */
  const roh = await antwort.text();
  try {
    return JSON.parse(roh) as T;
  } catch {
    throw new ApiFehler(
      antwort.status,
      'Die Antwort der Schnittstelle kam unvollständig an — sie bricht nach '
      + `${new Intl.NumberFormat('de-DE').format(roh.length)} Zeichen ab. `
      + 'Bitte erneut versuchen; hält es an, liegt es an der Übertragung, nicht an den Daten.',
    );
  }
}

/* ------------------------------------------------------- Eigene Vorgänge */

/**
 * Die Vorgangskennungen, die **dieser Reiter** geschrieben hat.
 *
 * Gebraucht von der Live-Abfrage (`api/live.ts`): sie soll nicht melden
 * „Diese Rolle wurde von … geändert", wenn die Änderung von hier kam. Die
 * Schnittstelle kennzeichnet eigene Einträge zwar mit `eigene` — das heißt
 * aber „dasselbe Konto", und dasselbe Konto in einem zweiten Fenster ist für
 * den, der vor dem ersten sitzt, sehr wohl ein anderer Bearbeiter. Die
 * Kennung unterscheidet beides.
 *
 * Begrenzt auf die letzten 500: mehr Schreibvorgänge in einer Sitzung sind
 * selten, und ein Satz, der unbegrenzt wächst, wäre ein Speicherleck in
 * einem Reiter, der den ganzen Tag offen steht.
 */
const EIGENE_VORGAENGE = new Set<string>();
const EIGENE_HOECHSTENS = 500;

function vorgangMerken(roh: unknown): void {
  if (typeof roh !== 'string' || roh === '') return;
  /* Kleingeschrieben: die Änderungsabfrage liefert die Kennung so, die
     Schreibwege nicht immer. */
  const kennung = roh.toLowerCase();
  EIGENE_VORGAENGE.delete(kennung);
  EIGENE_VORGAENGE.add(kennung);
  if (EIGENE_VORGAENGE.size > EIGENE_HOECHSTENS) {
    const aeltester = EIGENE_VORGAENGE.values().next().value;
    if (aeltester !== undefined) EIGENE_VORGAENGE.delete(aeltester);
  }
}

/** Kam dieser Vorgang aus diesem Reiter? */
export function eigenerVorgang(kennung: string | null | undefined): boolean {
  return !!kennung && EIGENE_VORGAENGE.has(kennung.toLowerCase());
}

/* ---------------------------------------------------------------- Schreiben */

/**
 * Ein schreibender Aufruf — dieselben Koepfe, dieselbe Fehlerbehandlung wie
 * `holen`, nur mit Methode und Rumpf.
 *
 * Bis zum 13.08.2026 stand diese Funktion dreimal fast gleich in
 * `api/rechte.ts`, `api/kontorollen.ts` und (als vierte Kopie geplant) hier.
 * `kontorollen.ts` hielt das ausdruecklich als Mangel fest. Sie steht jetzt an
 * einer Stelle: eine Verbesserung an der Fehlerbehandlung — etwa die Meldung
 * fuer eine abgeschnittene Antwort — wirkt sonst nur in einem Drittel der
 * Schreibwege.
 *
 * Bewusst **kein** Haken. `useAbruf` haengt an einem Rendervorgang; eine
 * Rechtevergabe oder eine neue Regel laeuft genau dann, wenn jemand sie
 * ausloest.
 */
export async function senden<T>(
  pfad: string,
  methode: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  optionen: Optionen & {
    rumpf?: unknown;
    /**
     * Weitere Koepfe, etwa `if-match` beim Aendern.
     *
     * Ausdruecklich als eigenes Feld und nicht als frei zusammengesetzter
     * Kopfsatz: `x-mandant` und `authorization` setzt dieser Klient, und wer
     * sie von aussen ueberschreiben koennte, koennte damit den Mandanten
     * wechseln.
     */
    zusatzkopf?: Record<string, string>;
  } = {},
): Promise<T> {
  /* Im Rueckblick wird nicht geschrieben.
   *
   * Das Netz vor dem Netz: die Schnittstelle weist jeden schreibenden Weg
   * ohnehin ab (Haken in `apps/api/src/server.ts`), und die Oberflaeche
   * blendet ihre Schaltflaechen aus, weil `/api/ich` im Rueckblick nur
   * lesende Rechte meldet. Bleibt ein Weg uebrig, den beides nicht
   * erwischt — ein Tastenkuerzel, eine offene Schublade —, endet er hier
   * und nicht in einer Fehlermeldung vom Server. */
  if (stichtag) {
    throw new Error(
      'Im Rückblick wird nicht geschrieben. Zurück auf „heute", dann geht es.');
  }

  const kopf: Record<string, string> = {
    accept: 'application/json',
    'content-type': 'application/json',
    ...(optionen.zusatzkopf ?? {}),
  };
  if (optionen.mandant) kopf['x-mandant'] = optionen.mandant;
  if (optionen.token) kopf['authorization'] = `Bearer ${optionen.token}`;
  if (import.meta.env.DEV) {
    try {
      const konto = localStorage.getItem('iammatrix.konto');
      if (konto) kopf['x-konto'] = konto;
    } catch { /* kein Speicher verfuegbar */ }
  }

  kopfPruefen(kopf, methode, pfad);

  /* Eine mitgeschickte Vorgangskennung ist ein eigener Vorgang — auch wenn
     die Antwort ausbleibt. Siehe `eigenerVorgang`. */
  vorgangMerken((optionen.rumpf as { vorgangId?: unknown } | undefined)?.vorgangId);

  let antwort: Response;
  try {
    antwort = await fetch(`${BASIS}${pfad}`, {
      method: methode,
      headers: kopf,
      body: JSON.stringify(optionen.rumpf ?? {}),
      ...(optionen.signal ? { signal: optionen.signal } : {}),
    });
  } catch (fehler) {
    if (fehler instanceof DOMException && fehler.name === 'AbortError') throw fehler;
    throw new ApiFehler(0, netzfehler(methode, pfad, fehler));
  }

  if (!antwort.ok) {
    let text = `Die Schnittstelle antwortete mit ${antwort.status}.`;
    try {
      const koerper = (await antwort.json()) as { fehler?: string };
      if (koerper.fehler) text = koerper.fehler;
    } catch {
      // Antwort ohne JSON-Körper — die Vorgabe oben bleibt stehen
    }
    throw new ApiFehler(antwort.status, text);
  }

  /* 204 hat keinen Rumpf. `antwort.json()` wirft darauf einen Syntaxfehler —
     und der laese sich von einer abgeschnittenen Antwort nicht unterscheiden. */
  const roh = await antwort.text();
  if (!roh) return undefined as T;
  try {
    const gelesen = JSON.parse(roh) as T;
    vorgangMerken((gelesen as { vorgangId?: unknown } | null)?.vorgangId);
    return gelesen;
  } catch {
    throw new ApiFehler(
      antwort.status,
      'Die Antwort der Schnittstelle kam unvollständig an — sie bricht nach '
      + `${new Intl.NumberFormat('de-DE').format(roh.length)} Zeichen ab.`,
    );
  }
}

/* ------------------------------------------------------------------ Wecken */

/**
 * Weckt die serverlose Datenbank, bevor die erste Liste angefordert wird.
 * Läuft absichtlich ohne Fehlerbehandlung ins Leere: schlägt es fehl, meldet
 * das die erste echte Anfrage ohnehin — hier soll nur Zeit gewonnen werden.
 */
export function wecken(): void {
  void fetch(`${BASIS}/bereit`).catch(() => undefined);
}
